import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Wifi, BatteryCharging, Coffee, BookOpen, MapPin, Shield, 
  Laptop, Clock, Users, Building2, ArrowRight, CheckCircle,
  Headphones, Camera, FileText, Award, TrendingUp
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import EzoicAd from "../components/EzoicAd";

const GUIDES = [
  {
    id: "wifi-requirements",
    title: "WiFi Speed Requirements for Remote Work",
    description: "Understanding what internet speeds you actually need for video calls, file sharing, and productive remote work.",
    icon: Wifi,
    color: "teal",
    content: `
      <h2>What WiFi Speed Do You Actually Need?</h2>
      <p>Not all remote work requires the same bandwidth. Here's our comprehensive breakdown:</p>
      
      <h3>Basic Tasks (5-15 Mbps)</h3>
      <ul>
        <li>Email and messaging</li>
        <li>Document editing (Google Docs, Word Online)</li>
        <li>Light web browsing</li>
        <li>Audio-only calls</li>
      </ul>
      
      <h3>Standard Remote Work (25-50 Mbps)</h3>
      <ul>
        <li>Video conferencing (Zoom, Teams, Meet)</li>
        <li>Screen sharing</li>
        <li>Cloud file syncing</li>
        <li>Streaming music while working</li>
      </ul>
      
      <h3>Heavy Professional Use (50-100+ Mbps)</h3>
      <ul>
        <li>Multiple simultaneous video calls</li>
        <li>Large file uploads/downloads</li>
        <li>Software development with cloud tools</li>
        <li>Design work with cloud assets</li>
      </ul>
      
      <h3>Upload Speed Matters Too</h3>
      <p>Many venues advertise download speeds but ignore upload. For video calls, you need at least:</p>
      <ul>
        <li><strong>720p video:</strong> 1.5 Mbps upload</li>
        <li><strong>1080p video:</strong> 3 Mbps upload</li>
        <li><strong>Screen sharing:</strong> 3-5 Mbps upload</li>
      </ul>
      
      <h3>Strider's Verified Speed Testing</h3>
      <p>Unlike other platforms, Strider conducts actual speed tests using M-Lab NDT7 technology. Our listed speeds are verified by real users, not advertised by venues.</p>
    `,
    readTime: "5 min read"
  },
  {
    id: "workspace-etiquette",
    title: "Remote Work Etiquette in UK Cafes",
    description: "The unwritten rules of working from cafes, pubs, and public spaces in Britain without being 'that person'.",
    icon: Coffee,
    color: "amber",
    content: `
      <h2>The Social Contract of Cafe Working</h2>
      <p>Working from cafes is a privilege, not a right. Here's how to do it respectfully:</p>
      
      <h3>Purchase Expectations</h3>
      <ul>
        <li><strong>Minimum spend:</strong> At least one item per hour is standard courtesy</li>
        <li><strong>Peak hours:</strong> Avoid lunch rush (12-2pm) for long work sessions</li>
        <li><strong>Table size:</strong> Don't occupy a 4-person table alone during busy times</li>
      </ul>
      
      <h3>Technology Etiquette</h3>
      <ul>
        <li><strong>Video calls:</strong> Use headphones or find a quiet corner</li>
        <li><strong>Sound:</strong> Keep notifications muted or on vibrate</li>
        <li><strong>Power:</strong> Don't monopolise all available sockets</li>
      </ul>
      
      <h3>Space Awareness</h3>
      <ul>
        <li>Keep your equipment contained to your table</li>
        <li>Don't spread papers across multiple surfaces</li>
        <li>Be prepared to share tables if needed</li>
      </ul>
      
      <h3>Communication</h3>
      <ul>
        <li>Ask before taking a call if the space is quiet</li>
        <li>Thank staff for letting you work there</li>
        <li>Leave a good tip if you've stayed for hours</li>
      </ul>
      
      <h3>Know When to Leave</h3>
      <p>If you've been there 3+ hours and a queue has formed, it's time to move on or order significantly more.</p>
    `,
    readTime: "4 min read"
  },
  {
    id: "finding-power-outlets",
    title: "Finding Power Outlets: The Remote Worker's Guide",
    description: "Strategic tips for locating and securing access to electrical sockets when working away from home.",
    icon: BatteryCharging,
    color: "green",
    content: `
      <h2>The Great British Socket Hunt</h2>
      <p>Running out of battery mid-meeting is a remote worker's nightmare. Here's how to avoid it:</p>
      
      <h3>Where to Look</h3>
      <ul>
        <li><strong>Window seats:</strong> Often have floor outlets nearby</li>
        <li><strong>Against walls:</strong> Wall-mounted sockets at seat height</li>
        <li><strong>Near the counter:</strong> Some venues have dedicated work areas</li>
        <li><strong>Hotel lobbies:</strong> Usually well-equipped for business travellers</li>
      </ul>
      
      <h3>Essential Kit</h3>
      <ul>
        <li><strong>Extension lead:</strong> A compact 2-3m extension opens up options</li>
        <li><strong>Multi-plug adapter:</strong> Share access and make friends</li>
        <li><strong>Portable charger:</strong> 20,000mAh minimum for laptop top-ups</li>
      </ul>
      
      <h3>Strider's Power Rating</h3>
      <p>Every venue on Strider includes power availability ratings:</p>
      <ul>
        <li><strong>Excellent:</strong> Outlets at every seat</li>
        <li><strong>Good:</strong> Several accessible outlets</li>
        <li><strong>Limited:</strong> Few outlets, arrive early</li>
        <li><strong>None:</strong> Bring a full battery</li>
      </ul>
      
      <h3>Pro Tips</h3>
      <ul>
        <li>Arrive early to claim powered seats</li>
        <li>Ask staff—some venues have hidden outlets</li>
        <li>Fully charge devices before leaving home</li>
        <li>Use battery saver modes proactively</li>
      </ul>
    `,
    readTime: "4 min read"
  },
  {
    id: "video-call-locations",
    title: "Best Locations for Video Calls",
    description: "How to find quiet, professional-looking backgrounds for important video meetings when you're not in an office.",
    icon: Camera,
    color: "blue",
    content: `
      <h2>Looking Professional on Camera</h2>
      <p>Not every workspace is suitable for video calls. Here's what to look for:</p>
      
      <h3>Background Considerations</h3>
      <ul>
        <li><strong>Plain walls:</strong> Bookshelf or neutral background preferred</li>
        <li><strong>Lighting:</strong> Face a window, not away from it</li>
        <li><strong>Movement:</strong> Avoid high-traffic areas behind you</li>
        <li><strong>Branding:</strong> Avoid competitor logos or distracting signage</li>
      </ul>
      
      <h3>Sound Quality</h3>
      <ul>
        <li><strong>Noise level:</strong> Below 50dB for clear audio</li>
        <li><strong>Echo:</strong> Carpeted areas absorb sound better</li>
        <li><strong>Interruptions:</strong> Avoid areas with tannoy announcements</li>
      </ul>
      
      <h3>Best Venue Types for Calls</h3>
      <ol>
        <li><strong>Hotel business centres:</strong> Purpose-built for professional use</li>
        <li><strong>Library study rooms:</strong> Bookable quiet spaces</li>
        <li><strong>Co-working spaces:</strong> Phone booth pods available</li>
        <li><strong>Quiet cafes:</strong> Off-peak hours only</li>
      </ol>
      
      <h3>Emergency Solutions</h3>
      <ul>
        <li>Virtual backgrounds (ensure good lighting)</li>
        <li>Blur feature on most platforms</li>
        <li>Audio-only with camera off</li>
        <li>Reschedule if environment is unsuitable</li>
      </ul>
    `,
    readTime: "5 min read"
  },
  {
    id: "security-public-wifi",
    title: "Staying Secure on Public WiFi",
    description: "Essential security practices for protecting your data when working from cafes, hotels, and public spaces.",
    icon: Shield,
    color: "red",
    content: `
      <h2>Protecting Your Data in Public</h2>
      <p>Public WiFi is convenient but carries risks. Here's how to stay safe:</p>
      
      <h3>Essential Protections</h3>
      <ul>
        <li><strong>VPN:</strong> Always use a reputable VPN service</li>
        <li><strong>HTTPS:</strong> Ensure all websites show the padlock icon</li>
        <li><strong>Two-factor auth:</strong> Enable on all important accounts</li>
        <li><strong>Firewall:</strong> Keep your device firewall enabled</li>
      </ul>
      
      <h3>Risky Activities to Avoid</h3>
      <ul>
        <li>Online banking without VPN</li>
        <li>Entering passwords on HTTP sites</li>
        <li>File sharing on open networks</li>
        <li>Leaving devices unattended</li>
      </ul>
      
      <h3>Network Verification</h3>
      <ul>
        <li>Ask staff for the exact network name</li>
        <li>Beware of similarly-named fake networks</li>
        <li>Prefer password-protected networks</li>
        <li>Check for WPA2/WPA3 encryption</li>
      </ul>
      
      <h3>Device Settings</h3>
      <ul>
        <li>Disable auto-connect to open networks</li>
        <li>Turn off file sharing</li>
        <li>Use "Public Network" settings</li>
        <li>Forget networks after use</li>
      </ul>
      
      <h3>Strider's Security Ratings</h3>
      <p>We assess venue WiFi security and note:</p>
      <ul>
        <li>Encryption type (WPA2/WPA3)</li>
        <li>Password protection</li>
        <li>Network isolation</li>
        <li>Terms of service requirements</li>
      </ul>
    `,
    readTime: "6 min read"
  },
  {
    id: "productivity-tips",
    title: "Maximising Productivity Away From Home",
    description: "Proven strategies for staying focused and productive when working from third workspaces.",
    icon: TrendingUp,
    color: "purple",
    content: `
      <h2>The Science of Third Workspace Productivity</h2>
      <p>Research shows that changing environments can boost creativity and focus. Here's how to make the most of it:</p>
      
      <h3>Choosing the Right Space</h3>
      <ul>
        <li><strong>Match task to environment:</strong> Creative work = busier cafes; focused work = quiet libraries</li>
        <li><strong>Time your visit:</strong> Know your peak productivity hours and plan accordingly</li>
        <li><strong>Have alternatives:</strong> Keep 2-3 backup locations in mind</li>
      </ul>
      
      <h3>Setting Up for Success</h3>
      <ul>
        <li>Arrive with a clear task list</li>
        <li>Set up your workspace intentionally</li>
        <li>Use noise-cancelling headphones</li>
        <li>Position your screen away from distractions</li>
      </ul>
      
      <h3>Managing Energy</h3>
      <ul>
        <li><strong>Hydration:</strong> Order water alongside coffee</li>
        <li><strong>Breaks:</strong> Use the Pomodoro technique (25 min work, 5 min break)</li>
        <li><strong>Movement:</strong> Stand up and stretch regularly</li>
        <li><strong>Nutrition:</strong> Choose brain-fueling snacks over sugar</li>
      </ul>
      
      <h3>The "Deep Work" Session</h3>
      <ol>
        <li>Arrive with devices fully charged</li>
        <li>Set phone to Do Not Disturb</li>
        <li>Close unnecessary browser tabs</li>
        <li>Commit to 90 minutes of focused work</li>
        <li>Reward yourself afterward</li>
      </ol>
      
      <h3>Tracking What Works</h3>
      <p>Keep notes on which venues work best for different tasks. Strider lets you save favourites and add personal notes to venues.</p>
    `,
    readTime: "5 min read"
  }
];

const colorClasses = {
  teal: "bg-teal-50 text-teal-600 border-teal-200",
  amber: "bg-amber-50 text-amber-600 border-amber-200",
  green: "bg-green-50 text-green-600 border-green-200",
  blue: "bg-blue-50 text-blue-600 border-blue-200",
  red: "bg-red-50 text-red-600 border-red-200",
  purple: "bg-purple-50 text-purple-600 border-purple-200"
};

export default function GuidesPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Remote Work Guides | Strider - Expert Tips for Working from Anywhere"
        description="Comprehensive guides for remote workers: WiFi speed requirements, cafe etiquette, security tips, and productivity strategies for working from third workspaces."
        keywords="remote work guide, cafe work tips, public wifi security, workspace productivity, UK remote working"
        canonical="/guides"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-1.5 bg-teal-500/20 text-teal-300 rounded-full text-sm font-medium mb-6">
              Expert Resources
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Remote Work <span className="text-teal-400">Guides</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Everything you need to know about working productively from cafes, hotels, libraries, and third workspaces across the UK.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Guides Grid */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {GUIDES.map((guide, index) => (
              <motion.article
                key={guide.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link 
                  to={`/guides/${guide.id}`}
                  className="block h-full bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg hover:border-gray-300 transition-all group"
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${colorClasses[guide.color]}`}>
                    <guide.icon className="w-6 h-6" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                    {guide.title}
                  </h2>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {guide.description}
                  </p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">{guide.readTime}</span>
                    <span className="text-teal-600 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                      Read Guide <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Ready to find your perfect workspace?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Browse our verified database of work-friendly venues with real WiFi speeds, power availability, and user reviews.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/browse"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors"
            >
              <MapPin className="w-5 h-5" />
              Browse Workspaces
            </Link>
            <Link 
              to="/add-location"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Building2 className="w-5 h-5" />
              Add a Venue
            </Link>
          </div>
        </div>
      </section>

      {/* Ezoic Ad - Before footer on guides page */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <EzoicAd placementId={104} />
      </div>

      <Footer />
    </div>
  );
}

export { GUIDES };
